import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import { SentryErrorSources } from "constants/index";
import {
  ADD_PROJECT_DESIGN_SCOPE_RESPONSIBILITY,
  GET_PROJECT_DESIGN_SCOPE_RESPONSIBILITY
} from "pages/project-setting-menu/module-settings/design-scope-settings/queries";
import ErrorManager from "utils/cumulative-error-message/error-manager";
import {
  findResponsibilityOption,
  responsibilitiesFromEnum,
  type AddDesignScopeResponsibilityResult,
  type DesignScopeResponsibilityEnumValue,
  type DesignScopeResponsibilityOption
} from "../shared/design-scope-responsibilities";

type ProjectGqlClient = {
  query: (options: {
    query: unknown;
    fetchPolicy?: "network-only";
  }) => Promise<{
    data?: {
      get_project_design_scope_responsibility?: {
        enum_values?: DesignScopeResponsibilityEnumValue[] | null;
      } | null;
    };
    errors?: ReadonlyArray<{ message?: string }>;
  }>;
  mutate: (options: {
    mutation: unknown;
    variables?: { name: string };
  }) => Promise<{
    data?: {
      add_project_design_scope_responsibility?: {
        message?: string | null;
        result?: {
          enum_values?: DesignScopeResponsibilityEnumValue[] | null;
        } | null;
      } | null;
    };
    errors?: ReadonlyArray<{ message?: string }>;
  }>;
};

function asProjectGqlClient(client: unknown): ProjectGqlClient | null {
  if (!client || typeof client !== "object") return null;
  const candidate = client as { query?: unknown; mutate?: unknown };
  if (
    typeof candidate.query !== "function" ||
    typeof candidate.mutate !== "function"
  ) {
    return null;
  }
  return client as ProjectGqlClient;
}

function reportGraphqlError(error: unknown) {
  ErrorManager().handleError({
    error,
    errorSource: SentryErrorSources.graphql
  });
}

/**
 * Project Design Scope responsibilities (`get` / `add`).
 * Rename, delete, and the subscription copies live on the same query module
 * for the later settings screens.
 */
export function useDesignScopeResponsibilities(projectId: string | undefined): {
  options: DesignScopeResponsibilityOption[];
  names: string[];
  loading: boolean;
  /** False until the page has a project GraphQL client that can load the catalog. */
  catalogEnabled: boolean;
  addResponsibility: (
    name: string
  ) => Promise<AddDesignScopeResponsibilityResult>;
} {
  const { gqlClientForProject } = useContext(ProjectContext);
  const client = asProjectGqlClient(gqlClientForProject);
  const [options, setOptions] = useState<DesignScopeResponsibilityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const requestGen = useRef(0);

  useEffect(() => {
    if (!client || !projectId) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const gen = ++requestGen.current;
    let cancelled = false;
    setOptions([]);
    setLoading(true);
    client
      .query({
        query: GET_PROJECT_DESIGN_SCOPE_RESPONSIBILITY,
        fetchPolicy: "network-only"
      })
      .then((result) => {
        if (cancelled || gen !== requestGen.current) return;
        if (result.errors?.length) {
          throw new Error(result.errors[0]?.message || "Request failed");
        }
        setOptions(
          responsibilitiesFromEnum(
            result.data?.get_project_design_scope_responsibility?.enum_values
          )
        );
      })
      .catch((error: unknown) => {
        if (cancelled || gen !== requestGen.current) return;
        reportGraphqlError(error);
        setOptions([]);
      })
      .finally(() => {
        if (!cancelled && gen === requestGen.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, projectId]);

  const addResponsibility = useCallback(
    async (name: string): Promise<AddDesignScopeResponsibilityResult> => {
      const trimmed = name.trim();
      const existing = findResponsibilityOption(optionsRef.current, trimmed);
      if (existing) {
        return { success: true, name: existing.name, id: existing.id };
      }
      if (!client || !trimmed) return { success: false, name: "", id: "" };
      try {
        const result = await client.mutate({
          mutation: ADD_PROJECT_DESIGN_SCOPE_RESPONSIBILITY,
          variables: { name: trimmed }
        });
        if (result.errors?.length) {
          throw new Error(result.errors[0]?.message || "Request failed");
        }
        const payload = result.data?.add_project_design_scope_responsibility;
        const next = responsibilitiesFromEnum(payload?.result?.enum_values);
        const saved = findResponsibilityOption(next, trimmed);
        if (!next.length || !saved) {
          message.error(payload?.message || "Unable to add responsibility.");
          return { success: false, name: "", id: "" };
        }
        requestGen.current += 1;
        setOptions(next);
        return { success: true, name: saved.name, id: saved.id };
      } catch (error) {
        reportGraphqlError(error);
        return { success: false, name: "", id: "" };
      }
    },
    [client]
  );

  return {
    options,
    names: options.map((option) => option.name),
    loading,
    catalogEnabled: Boolean(client),
    addResponsibility
  };
}
